import { useState, type ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useQuery } from '@tanstack/react-query';

/**
 * ZMIANA KLUCZA NATURALNEGO — jedno okno dla numeru karty, kodu grupy, numeru artykułu
 * i kodu grupy artykułów.
 *
 * ⚠️ WSPÓLNE, BO TO JEDNA OPERACJA W CZTERECH ODMIANACH. W SPS każdy z tych kluczy jest rozsiany
 * po kilku tabelach, a jego zmiana to kaskada opisana jedną mapą (`kaskada-klucza.ts`). Cztery
 * osobne okna znaczyłyby cztery miejsca, w których trzeba pamiętać o tym samym: że to nie jest
 * edycja pola, że kolizji nie wolno przepuścić i że operacja idzie do bazy klienta w całości albo
 * wcale.
 *
 * ⚠️ OSOBNE OKNO, A NIE POLE W FORMULARZU EDYCJI — i to jest reguła dla wszystkich czterech.
 * Klucz stojący obok nazwy znaczyłby, że literówka przy poprawianiu opisu uruchamia kaskadę.
 */
export type ZmianaKluczaProps = {
  tytul: string;
  /** Drugi wiersz nagłówka — zwykle „nazwa · obecny klucz". */
  podpis: string;
  wstep: string;
  ostrzezenie: string;
  etykietaPola: string;
  obecny: string;
  /** Ile znaków mieści klucz w SPS — SPS obciąłby dłuższy BEZ ostrzeżenia. */
  limit: number;
  /**
   * Sprawdzenie kolizji na bieżąco. `pending` = klucz wolny, ale czeka na niego operacja.
   * `powod` — własny komunikat zajętości zamiast ogólnego „należy już do…”, gdy odmiana wie więcej.
   */
  sprawdz: (
    wartosc: string,
  ) => Promise<{ free: boolean; takenBy: { name: string } | null; pending?: boolean; powod?: string }>;
  /** Opcjonalny drugi przełącznik (u pracownika: historia wydań). */
  dodatkowy?: { etykieta: string; podpowiedz: string; wartosc: boolean; ustaw: (v: boolean) => void };
  /**
   * Wyliczony zakres operacji, pokazywany NAD polem.
   *
   * ⚠️ NIE OZDOBA, TYLKO WARUNEK DECYZJI D1. Przy towarach i grupach towarowych kaskada SPS
   * przepisuje `Rationing.ITEMKEY` bez filtru po `ITEMTYPE`, więc rusza też wiersze, których
   * operacja nie dotyczy. Zostajemy przy parytecie z SPS pod warunkiem, że te wiersze widać
   * PRZED kliknięciem — bo inaczej podgląd zaniża zakres, a to ta sama klasa błędu co luka G7.
   */
  podglad?: ReactNode;
  /** Wykonanie zmiany; zwraca tekst potwierdzenia do paska. */
  zapisz: (nowy: string) => Promise<string>;
  onClose: () => void;
  onSaved: () => void;
};

export function ZmianaKlucza({
  tytul,
  podpis,
  wstep,
  ostrzezenie,
  etykietaPola,
  obecny,
  limit,
  sprawdz,
  dodatkowy,
  podglad,
  zapisz,
  onClose,
  onSaved,
}: ZmianaKluczaProps) {
  const { t } = useI18n();
  const toast = useToast();
  const [nowy, setNowy] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wpisany = nowy.trim();
  const tenSam = wpisany.toLowerCase() === obecny.toLowerCase();
  const zaDlugi = wpisany.length > limit;

  /**
   * ⚠️ ZNAKI SPRAWDZANE JUŻ TUTAJ, mimo że API i tak odmówi. Odmowa po wysłaniu każe zgadywać,
   * który znak zawinił; tu widać to w chwili wpisywania, razem z konkretnym znakiem.
   */
  const zleZnaki = [...new Set([...wpisany].filter((z) => z.codePointAt(0)! < 0x20 || z.codePointAt(0)! > 0x7e))];

  const kolizja = useQuery({
    queryKey: ['klucz', 'kolizja', tytul, wpisany],
    queryFn: () => sprawdz(wpisany),
    enabled: wpisany.length > 0 && !tenSam && zleZnaki.length === 0 && !zaDlugi,
  });

  const zajety = kolizja.data && !kolizja.data.free;
  const mozna = wpisany.length > 0 && !tenSam && !zajety && !saving && zleZnaki.length === 0 && !zaDlugi;

  const wykonaj = async () => {
    setSaving(true);
    setError(null);
    try {
      toast.success(await zapisz(wpisany));
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={tytul} description={podpis}>
        <FormGrid>
          <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">{wstep}</div>

          {podglad}

          <Field label={etykietaPola} htmlFor="nowyKlucz" required>
            <Input
              id="nowyKlucz"
              autoComplete="off"
              autoFocus
              placeholder={obecny}
              value={nowy}
              onChange={(e) => setNowy(e.target.value)}
            />
            {tenSam && wpisany.length > 0 && (
              <p className="mt-1 text-xs text-[var(--warning)]">{t('klucz.taSama')}</p>
            )}
            {zaDlugi && (
              <p className="mt-1 text-xs text-[var(--destructive)]">
                {t('klucz.zaDlugi').replace('{n}', String(limit))}
              </p>
            )}
            {zleZnaki.length > 0 && (
              <p className="mt-1 text-xs text-[var(--destructive)]">
                {t('klucz.zleZnaki').replace('{znaki}', zleZnaki.join(' '))}
              </p>
            )}
            {zajety && (
              <p className="mt-1 text-xs text-[var(--destructive)]">
                {kolizja.data?.powod ?? t('klucz.zajety').replace('{name}', kolizja.data?.takenBy?.name ?? '?')}
              </p>
            )}
            {!zajety && kolizja.data?.free && kolizja.data.pending && (
              <p className="mt-1 text-xs text-[var(--warning)]">{t('klucz.wKolejce')}</p>
            )}
          </Field>

          {dodatkowy && (
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-3 text-sm hover:bg-accent/50">
              <input
                type="checkbox"
                className="mt-0.5 size-3.5 shrink-0 accent-[var(--primary)]"
                checked={dodatkowy.wartosc}
                onChange={(e) => dodatkowy.ustaw(e.target.checked)}
              />
              <span className="min-w-0">
                <span className="block leading-snug">{dodatkowy.etykieta}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{dodatkowy.podpowiedz}</span>
              </span>
            </label>
          )}

          <p className="flex items-start gap-1.5 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span>{ostrzezenie}</span>
          </p>

          <ModalError>{error}</ModalError>
        </FormGrid>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={!mozna} onClick={() => void wykonaj()}>
            {saving && <Loader2 className="animate-spin" />}
            {t('klucz.wykonaj')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
